import { usePageContext } from "vike-react/usePageContext";
import { useEffect, useRef, useState } from "react";
import { MotifBand } from "../../components/Motifs.js";
import { langCodeToName } from "../../utils/appTheme.js";

const C = {
  bg: 'var(--bg)', surf: 'var(--surf)', surf2: 'var(--surf2)',
  border: 'var(--border)', text: 'var(--text)', muted: 'var(--muted)', dim: 'var(--dim)',
};
const AF = {
  h: 'var(--font-h, "Space Grotesk", system-ui, sans-serif)',
  b: "'DM Sans', system-ui, sans-serif",
  m: "'DM Mono', 'Courier New', monospace",
};

const LANG_URL: Record<string, string> = { es: 'spanish', pt: 'portuguese', fi: 'finnish', fr: 'french', it: 'italian', ca: 'catalan', ro: 'romanian' };

function loadSetting(key: string, defaultVal: string): string {
  if (typeof window === 'undefined') return defaultVal;
  try { return localStorage.getItem(key) ?? defaultVal; } catch { return defaultVal; }
}
function saveSetting(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch {}
}

function InlineEdit({ value, onChange, placeholder, style }: { value: string; onChange: (v: string) => void; placeholder?: string; style?: React.CSSProperties }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  if (editing) {
    return <input autoFocus value={draft} onChange={e => setDraft(e.target.value)}
      onBlur={() => { setEditing(false); onChange(draft); }}
      onKeyDown={e => { if (e.key === 'Enter') { setEditing(false); onChange(draft); } if (e.key === 'Escape') { setEditing(false); setDraft(value); } }}
      style={{ fontFamily: AF.b, fontSize: 14, color: 'var(--text)', background: 'var(--surf)', border: '1px solid var(--accent-border)', borderRadius: 5, padding: '3px 8px', outline: 'none', minWidth: 160, ...style }}/>;
  }
  return <span onClick={() => setEditing(true)} style={{ fontFamily: AF.b, fontSize: 14, color: value ? 'var(--text)' : 'var(--muted)', cursor: 'text', padding: '3px 0', ...style }}>{value || placeholder || 'Click to edit'}</span>;
}

export default function Page() {
  const pageContext = usePageContext();
  const langCode = pageContext.routeParams?.language ?? 'es';
  const language = langCodeToName(langCode);

  const [isMobile, setIsMobile] = useState(false);
  const [name, setName] = useState(() => loadSetting('c8-settings-name', ''));
  const [email, setEmail] = useState(() => loadSetting('c8-settings-email', ''));
  const [goal, setGoal] = useState(() => loadSetting('c8-settings-goal', '20'));
  const [notif, setNotif] = useState(() => loadSetting('c8-settings-notif', 'true') === 'true');

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  useEffect(() => {
    fetch('/api/data/settings')
      .then(r => r.json())
      .then(({ settings }) => {
        if (!settings) return
        if (settings.name) { setName(settings.name); saveSetting('c8-settings-name', settings.name) }
        if (settings.goal) { setGoal(String(settings.goal)); saveSetting('c8-settings-goal', String(settings.goal)) }
        if (settings.notif !== undefined) { setNotif(settings.notif); saveSetting('c8-settings-notif', settings.notif ? 'true' : 'false') }
      })
      .catch(() => {})
  }, [])

  const settingsSaveRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  function pushSettings(patch: Record<string, unknown>) {
    if (settingsSaveRef.current) clearTimeout(settingsSaveRef.current)
    settingsSaveRef.current = setTimeout(() => {
      fetch('/api/data/settings', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(patch),
      }).catch(() => {})
    }, 1000)
  }

  const hp = isMobile ? '16px' : '48px';

  function handleLanguageChange(code: string) {
    const name = LANG_URL[code] ?? 'spanish';
    window.location.href = `/learn-${name}/settings`;
  }

  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${C.border}`, gap: 12, flexWrap: 'wrap' }}>
      <span style={{ fontFamily: AF.b, fontSize: 14, color: C.text, fontWeight: 500 }}>{label}</span>
      {children}
    </div>
  );

  const SectionHead = ({ title }: { title: string }) => (
    <div style={{ fontFamily: AF.m, fontSize: 10, color: C.dim, letterSpacing: '0.1em', textTransform: 'uppercase', margin: '28px 0 4px', paddingBottom: 8, borderBottom: `1px solid ${C.border}` }}>{title}</div>
  );

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: `32px ${hp}`, background: C.bg }}>
      <h1 style={{ fontFamily: AF.h, fontSize: 26, fontWeight: 700, color: C.text, margin: '0 0 4px', letterSpacing: '-0.7px' }}>Settings</h1>
      <p style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, margin: 0 }}>Manage your account and practice preferences.</p>
      <div style={{ marginTop: 16, marginBottom: 4 }}>
        <MotifBand language={language} height={28} opacity={0.45} fade/>
      </div>

      <SectionHead title="Profile"/>
      <Row label="Name">
        <InlineEdit
          value={name}
          onChange={v => { setName(v); saveSetting('c8-settings-name', v); pushSettings({ name: v, goal: parseInt(goal), notif }) }}
          placeholder="Your name"
        />
      </Row>
      <Row label="Email">
        <InlineEdit
          value={email}
          onChange={v => { setEmail(v); saveSetting('c8-settings-email', v); }}
          placeholder="your@email.com"
        />
      </Row>
      <Row label="Plan">
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontFamily: AF.m, fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}>Free</span>
          <a href={`/learn-${LANG_URL[langCode] ?? 'spanish'}/pricing`} style={{
            fontFamily: AF.b, fontSize: 12, fontWeight: 700, background: 'var(--accent)', color: C.bg,
            padding: '4px 12px', borderRadius: 5, cursor: 'pointer', textDecoration: 'none',
          }}>Upgrade to Pro</a>
        </div>
      </Row>

      <SectionHead title="Language"/>
      <Row label="Target language">
        <div style={{ display: 'flex', gap: 3, background: C.surf, border: `1px solid ${C.border}`, borderRadius: 7, padding: 3, flexWrap: 'wrap' }}>
          {[['es','🇪🇸','Spanish'],['pt','🇧🇷','Portuguese'],['fi','🇫🇮','Finnish']].map(([code,flag,label]) => (
            <button key={code} onClick={() => handleLanguageChange(code)} style={{
              padding: '7px 14px', borderRadius: 5, border: 'none', cursor: 'pointer',
              background: langCode === code ? 'var(--accent)' : 'transparent',
              color: langCode === code ? C.bg : C.muted,
              fontFamily: AF.b, fontSize: 13, fontWeight: langCode === code ? 700 : 400,
              display: 'flex', alignItems: 'center', gap: 5,
            }}>{flag} {label}</button>
          ))}
          {[['it','🇮🇹','Italian'],['fr','🇫🇷','French'],['ca','🏴','Catalan']].map(([code,flag,label]) => (
            <div key={code} title="Coming soon" style={{
              padding: '7px 14px', borderRadius: 5,
              color: C.dim, fontFamily: AF.b, fontSize: 13,
              display: 'flex', alignItems: 'center', gap: 5,
              opacity: 0.45, cursor: 'default',
            }}>{flag} {label} <span style={{ fontSize: 10 }}>soon</span></div>
          ))}
        </div>
      </Row>
      <Row label="Dialect">
        <span style={{ fontFamily: AF.b, fontSize: 14, color: C.muted }}>
          {langCode === 'pt' ? 'Brazilian Portuguese' : langCode === 'fi' ? 'Standard Finnish' : 'Latin American'}
        </span>
      </Row>

      <SectionHead title="Practice"/>
      <Row label="Daily goal (verbs)">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', gap: 3, background: C.surf, border: `1px solid ${C.border}`, borderRadius: 7, padding: 3 }}>
            {['10','20','30','50'].map(g => (
              <button key={g} onClick={() => { setGoal(g); saveSetting('c8-settings-goal', g); pushSettings({ name, goal: parseInt(g), notif }) }} style={{
                padding: '5px 12px', borderRadius: 4, border: 'none', cursor: 'pointer',
                background: goal === g ? 'var(--accent)' : 'transparent',
                color: goal === g ? C.bg : C.muted,
                fontFamily: AF.m, fontSize: 13, fontWeight: goal === g ? 600 : 400,
              }}>{g}</button>
            ))}
          </div>
          <InlineEdit
            value={!['10','20','30','50'].includes(goal) ? goal : ''}
            onChange={v => { const n = parseInt(v); if (!isNaN(n) && n > 0) { setGoal(String(n)); saveSetting('c8-settings-goal', String(n)); pushSettings({ name, goal: n, notif }) } }}
            placeholder="custom"
            style={{ width: 64, textAlign: 'center' }}
          />
        </div>
      </Row>
      <Row label="Daily reminder">
        <div onClick={() => {
          const next = !notif;
          setNotif(next);
          saveSetting('c8-settings-notif', next ? 'true' : 'false');
          pushSettings({ name, goal: parseInt(goal), notif: next });
        }} style={{
          width: 44, height: 24, borderRadius: 12, position: 'relative', cursor: 'pointer',
          background: notif ? 'var(--accent)' : C.surf2,
          border: `1px solid ${notif ? 'var(--accent)' : C.border}`,
          transition: 'background 0.2s',
        }}>
          <div style={{
            position: 'absolute', top: 3, left: notif ? 22 : 3, width: 16, height: 16,
            borderRadius: '50%', background: notif ? C.bg : C.muted,
            transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          }}/>
        </div>
      </Row>

      <SectionHead title="Account"/>
      <Row label="Export progress data">
        <button onClick={async () => {
          try {
            // First fetch server export, then merge with localStorage
            const serverRes = await fetch('/api/data/export').catch(() => null)
            const serverData = serverRes?.ok ? await serverRes.json() : null
            const localData: Record<string, unknown> = {}
            for (let i = 0; i < localStorage.length; i++) {
              const k = localStorage.key(i)!
              try { localData[k] = JSON.parse(localStorage.getItem(k)!) } catch { localData[k] = localStorage.getItem(k) }
            }
            const data = { local: localData, server: serverData, exportedAt: Date.now() }
            const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }))
            const a = document.createElement('a')
            a.href = url; a.download = 'conjugr8-data-export.json'; a.click()
            URL.revokeObjectURL(url)
          } catch {}
        }} style={{
          fontFamily: AF.b, fontSize: 13, fontWeight: 600,
          background: C.surf2, color: C.text,
          border: `1px solid ${C.border}`, padding: '6px 16px', borderRadius: 6, cursor: 'pointer',
        }}>Download JSON</button>
      </Row>
      <Row label="Delete all progress">
        <button onClick={() => {
          if (window.confirm('Permanently delete all local progress data? This cannot be undone.')) {
            try {
              const keep = ['c8-settings-name','c8-settings-email','c8-settings-goal','c8-settings-notif','language','theme']
              const preserved: Record<string, string | null> = {}
              keep.forEach(k => { preserved[k] = localStorage.getItem(k) })
              localStorage.clear()
              keep.forEach(k => { if (preserved[k] !== null) localStorage.setItem(k, preserved[k]!) })
            } catch {}
            window.location.reload()
          }
        }} style={{
          fontFamily: AF.b, fontSize: 13, fontWeight: 600,
          background: 'transparent', color: 'var(--error)',
          border: `1px solid var(--error-border)`, padding: '6px 16px', borderRadius: 6, cursor: 'pointer',
        }}>Reset progress</button>
      </Row>
      <Row label="Delete account">
        <button
          onClick={async () => {
            if (!confirm('This will permanently delete all your Conjugr8 data. This cannot be undone. Continue?')) return
            const res = await fetch('/api/data/account', { method: 'DELETE' })
            if (res.ok) {
              localStorage.clear()
              window.location.href = '/api/auth/signout'
            } else {
              alert('Failed to delete account. Please try again or contact support.')
            }
          }}
          style={{ fontFamily: AF.b, fontSize: 13, color: '#e05535', background: 'transparent', border: '1px solid #e05535', borderRadius: 6, padding: '6px 14px', cursor: 'pointer' }}
        >
          Delete account
        </button>
      </Row>
    </div>
  );
}
